import { CustomHeaderProps } from 'ag-grid-react';
import { ColDef, IRowNode } from 'ag-grid-community';
import styled, { createGlobalStyle } from '@benzinga/themetron';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';

import { faArrowUp, faArrowDown, faArrowsUpDown } from '@fortawesome/pro-solid-svg-icons';
import { noop } from '@benzinga/utils';

export interface MyCustomHeaderProps extends CustomHeaderProps {
  menuIcon: string;
}

type AbsSortTypes = 'asc' | 'desc' | 'desc-abs' | null;

export type AdvancedSortConfig =
  | {
      absSort: true;
      localSort?: boolean;
      onSetSort?: (sort: 'asc' | 'desc' | 'desc-abs' | null) => void;
      val: 'asc' | 'desc' | 'desc-abs' | null;
    }
  | {
      absSort: false;
      localSort?: boolean;
      onSetSort?: (sort: 'asc' | 'desc' | null) => void;
      val: 'asc' | 'desc' | null;
    };

type AdvancedSortInternal = AdvancedSortConfig & {
  comparator?: ColDef['comparator'];
};

type AdvancedColDefContext = {
  advancedSort?: AdvancedSortInternal;
};

const absCompare = (comparator: AdvancedColDef['comparator']) =>
  comparator
    ? (valueA: any, valueB: any, nodeA: IRowNode<any>, nodeB: IRowNode<any>, isDescending: boolean) =>
        comparator(Math.abs(valueA), Math.abs(valueB), nodeA, nodeB, isDescending)
    : (valueA: any, valueB: any) => (valueA === valueB ? 0 : Math.abs(valueA) > Math.abs(valueB) ? 1 : -1);

/** Read advancedSort from colDef.context (AG Grid–valid). Falls back to legacy top-level for builders. */
export const getAdvancedSort = (colDef: ColDef | AdvancedColDef): AdvancedSortInternal | undefined => {
  const fromContext = (colDef.context as AdvancedColDefContext | undefined)?.advancedSort;
  if (fromContext) return fromContext;
  return (colDef as AdvancedColDef).advancedSort as AdvancedSortInternal | undefined;
};

type AdvancedSortPatch = {
  absSort?: boolean;
  comparator?: ColDef['comparator'];
  localSort?: boolean;
  onSetSort?: (sort: 'asc' | 'desc' | 'desc-abs' | null) => void;
  val?: AbsSortTypes;
};

/**
 * Merge advancedSort into colDef.context and strip any top-level `advancedSort`
 * (AG Grid v32+ warns on unknown colDef keys — use context for app data).
 */
export const withAdvancedSort = (colDef: AdvancedColDef, advancedSort: AdvancedSortPatch): AdvancedColDef => {
  const { advancedSort: _legacy, ...rest } = colDef;
  const prev = getAdvancedSort(colDef);
  return {
    ...rest,
    context: {
      ...((rest.context as object) ?? {}),
      advancedSort: {
        absSort: false,
        localSort: true,
        onSetSort: noop,
        val: null,
        ...prev,
        ...advancedSort,
      } as AdvancedSortInternal,
    },
  };
};

export const absColDef = (colDef: AdvancedColDef): AdvancedColDef => {
  const { advancedSort: inputSort, context, ...rest } = colDef;
  const prevContextSort = (context as AdvancedColDefContext | undefined)?.advancedSort;
  // Cast: spreads may set absSort:true + val:'desc-abs'; annotated false-default
  // would otherwise narrow `val` and make the desc-abs check a TS2367.
  const advancedSort = {
    absSort: false,
    localSort: true,
    onSetSort: noop,
    val: null,
    ...((inputSort as object) ?? {}),
    ...((prevContextSort as object) ?? {}),
    comparator: colDef.comparator,
  } as AdvancedSortInternal;

  return {
    ...rest,
    comparator: (advancedSort.val as AbsSortTypes) === 'desc-abs' ? absCompare(colDef.comparator) : colDef.comparator,
    context: {
      ...((context as object) ?? {}),
      advancedSort,
    },
    headerClass: 'bz-ag-grid-header',
    headerComponent: AbsColHeader,
    sort: null,
  };
};

export type AdvancedColDef = ColDef & {
  /**
   * Builder-only input. `absColDef` / `withAdvancedSort` move this onto
   * `colDef.context.advancedSort` so AG Grid does not warn about unknown keys.
   */
  advancedSort?: AdvancedSortConfig;
};

export const AbsColHeader = (props: MyCustomHeaderProps) => {
  const getSortValue = React.useCallback(() => {
    const colDef = props.column.getColDef() as AdvancedColDef;
    const advancedSort = getAdvancedSort(colDef);
    if (advancedSort) {
      return advancedSort.val;
    } else {
      return props.column.getSort() ?? null;
    }
  }, [props.column]);

  const [sort, setSort] = useState<AbsSortTypes>(() => getSortValue());

  const refButton = useRef(null);

  const onMenuClicked = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(
    event => {
      event.stopPropagation();
      props.showColumnMenu(refButton.current!);
    },
    [props],
  );

  const onSortClicked = React.useCallback(
    (event: any) => {
      const colDef = props.column.getColDef() as AdvancedColDef;
      const advancedSort = getAdvancedSort(colDef);
      if (advancedSort) {
        const newSort = (() => {
          if (advancedSort.absSort) {
            return sort === 'asc' ? 'desc' : sort === 'desc' ? 'desc-abs' : sort === 'desc-abs' ? null : 'asc';
          } else {
            return sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
          }
        })();
        // sets the sort for all abs columns
        props.api.getColumns()?.forEach(col => {
          const colDef2 = col.getColDef() as AdvancedColDef;
          const advancedSort2 = getAdvancedSort(colDef2);
          if (!advancedSort2) return;

          // Ensure context.advancedSort exists so mutations stick on the live colDef.
          if (!colDef2.context || typeof colDef2.context !== 'object') {
            colDef2.context = {};
          }
          const ctx = colDef2.context as AdvancedColDefContext;
          if (!ctx.advancedSort) ctx.advancedSort = { ...advancedSort2 };

          if (col.getColId() === props.column.getColId()) {
            ctx.advancedSort.val = newSort as AdvancedSortInternal['val'];
            colDef2.comparator =
              newSort === 'desc-abs' ? absCompare(ctx.advancedSort.comparator) : ctx.advancedSort.comparator;
          } else if (ctx.advancedSort.val !== null) {
            ctx.advancedSort.val = null;
            colDef2.comparator = ctx.advancedSort.comparator;
          }
        });

        advancedSort.localSort && props.setSort(newSort === 'desc-abs' ? 'desc' : newSort, event.shiftKey);
        if (advancedSort.onSetSort) {
          advancedSort.onSetSort(newSort as any);
        }
        setSort(newSort);
      } else {
        const newSort = sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
        props.setSort(newSort as any, event.shiftKey);
        setSort(newSort as any);
      }
    },
    [props, sort],
  );

  useEffect(() => {
    const myOnSortChanged = () => {
      const newSort = getSortValue();
      setSort(newSort);
    };
    props.column.addEventListener('sortChanged', myOnSortChanged);
    props.column.addEventListener('colDefChanged', myOnSortChanged);
    return () => {
      props.column.removeEventListener('sortChanged', myOnSortChanged);
      props.column.removeEventListener('colDefChanged', myOnSortChanged);
    };
  }, [getSortValue, props.api, props.column]);

  return (
    <>
      <Global />
      <Container
        className={
          `${sort ? `bz-ag-grid-header-${sort}` : ''} ${props.enableSorting ? `.bz-ag-grid-header-sort` : ''}` ||
          undefined
        }
        onClick={onSortClicked}
      >
        <span className="ag-header-icon ag-sort-order" data-ref="eSortOrder" />
        <Left>
          <Elipces>{props.displayName}</Elipces>
          {sort === 'asc' && <FontAwesomeIcon icon={faArrowUp} />}
          {sort === 'desc' && <FontAwesomeIcon icon={faArrowDown} />}
          {sort === 'desc-abs' && <FontAwesomeIcon icon={faArrowsUpDown} />}
        </Left>
        {props.enableMenu && (
          <div className={'menu'} onClick={onMenuClicked} ref={refButton}>
            <span aria-hidden="true" className="ag-header-icon ag-header-cell-menu-button">
              <span className="ag-icon ag-icon-menu" role="presentation" unselectable="on"></span>
            </span>
          </div>
        )}
      </Container>
    </>
  );
};

const Elipces = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  height: 16px;
`;

const Global = createGlobalStyle`
  .ag-header-cell-sortable {
    cursor: pointer;
  }

  .bz-ag-grid-header .menu {
    display: none;
  }

  .bz-ag-grid-header:hover {
    .menu {
      display: block;
    }
  }

  .bz-ag-grid-header:has(.bz-ag-grid-header-desc) {
    background: #11463b !important;
  }

  .bz-ag-grid-header:has(.bz-ag-grid-header-asc) {
    background: #4b1c19 !important;
  }

  .bz-ag-grid-header:has(.bz-ag-grid-header-desc-abs) {
    background: #141742 !important;
  }

`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const Left = styled.div`
  flex-grow: 2;
  text-overflow: ellipsis;
  overflow: hidden;
  display: flex;
  gap: 4px;
`;
